import type {
  WithContext,
  WebPage,
  Organization,
  BreadcrumbList,
  FAQPage,
  JobPosting,
  HowTo,
  SoftwareApplication,
  ItemList,
  Place,
  PostalAddress
} from 'schema-dts';
import { PUBLIC_URL } from '$env/static/public';
import { cdnUrl } from '$lib/utils/format';

function cdnUrlToString(url: string | null | undefined): string | undefined {
  return cdnUrl(url) ?? undefined;
}

const PREVIEW_URL = 'public-preview.'

export const defaultSEO = {
  title: 'TalentProfile - AI-Powered Tech Job Matching',
  description: 'Get personalized tech job matches delivered to your inbox. AI-powered matching for developers. Free forever.',
  canonical: PUBLIC_URL,
  openGraph: {
    type: 'website',
    url: PUBLIC_URL,
    title: 'TalentProfile - AI-Powered Tech Job Matching',
    description: 'Get personalized tech job matches delivered to your inbox.',
    image: `${PUBLIC_URL}/talentprofile-homepage-og.png`,
    images: [
      {
        url: `${PUBLIC_URL}/talentprofile-homepage-og.png`,
        width: 1200,
        height: 630,
        alt: 'TalentProfile'
      }
    ],
    site_name: 'TalentProfile'
  },
  twitter: {
    handle: '@talentprofile',
    site: '@talentprofile',
    image: `${PUBLIC_URL}/talentprofile-homepage-og.png`,
    cardType: 'summary_large_image' as const
  }
};

const REMOTE_TYPE_LABELS: Record<string, string> = {
  'global': 'Remote',
  'remote': 'Remote',
  'local-country': 'Remote',
  'local-city': 'Remote',
  'hybrid': 'Hybrid',
  'onsite': 'On-site'
};

const REMOTE_TYPE_SCHEMA: Record<string, string | null> = {
  global: 'TELECOMMUTE',
  remote: 'TELECOMMUTE',
  'local-country': 'TELECOMMUTE',
  'local-city': 'TELECOMMUTE',
  hybrid: 'TELECOMMUTE',
  onsite: null,
};

const EMPLOYMENT_TYPE_SCHEMA: Record<string, string> = {
  full_time: 'FULL_TIME',
  'full-time': 'FULL_TIME',
  fulltime: 'FULL_TIME',
  part_time: 'PART_TIME',
  'part-time': 'PART_TIME',
  contract: 'CONTRACTOR',
  contractor: 'CONTRACTOR',
  temporary: 'TEMPORARY',
  intern: 'INTERN',
  internship: 'INTERN',
};

function formatRemoteTypeLabel(raw: string): string {
  return REMOTE_TYPE_LABELS[raw] || raw;
}

function formatRemoteTypesForTitle(remoteTypes: string[]): string {
  const labels = new Set(remoteTypes.map(formatRemoteTypeLabel));
  if (labels.size === 0) return '';
  return ` | ${Array.from(labels).join(', ')}`;
}

function formatRemoteTypesForKeywords(remoteTypes: string[]): string[] {
  const labels = new Set(remoteTypes.map(formatRemoteTypeLabel));
  return Array.from(labels).map(label => `${label} positions`);
}

function safeParseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date;
}

export type StructuredData =
  | WithContext<WebPage>
  | WithContext<Organization>
  | WithContext<BreadcrumbList>
  | WithContext<FAQPage>
  | WithContext<JobPosting>
  | WithContext<HowTo>
  | WithContext<SoftwareApplication>
  | WithContext<ItemList>;

interface PageSEOOptions {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  structuredData?: StructuredData | StructuredData[];
  link?: Array<{ rel: string; href: string }>;
  additionalMetaTags?: Array<{ name: string; content: string }>;
}

export function createPageSEO(options: PageSEOOptions) {
  const {
    title,
    description,
    canonical,
    keywords,
    ogImage,
    ogType = 'website',
    structuredData,
    link = [],
    additionalMetaTags = []
  } = options;

  const fullCanonical = canonical
    ? canonical.startsWith('http')
      ? canonical
      : `${PUBLIC_URL}${canonical.startsWith('/') ? '' : '/'}${canonical}`.replace(/\/+$/, '')
    : defaultSEO.canonical;

  const fullOgImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${PUBLIC_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`)
    : defaultSEO.openGraph.images[0].url;

  const structuredDataArray: StructuredData[] = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return {
    title,
    description,
    canonical: fullCanonical.replace(PREVIEW_URL, ''),
    ...(keywords && {
      additionalMetaTags: [
        ...additionalMetaTags,
        { name: 'keywords', content: keywords }
      ]
    }),
    ...(!keywords && additionalMetaTags.length > 0 && { additionalMetaTags }),
    openGraph: {
      type: ogType,
      url: fullCanonical.replace(PREVIEW_URL, ''),
      title,
      description,
      image: fullOgImage,
      images: [
        {
          url: fullOgImage,
          width: 1200,
          height: 630,
          alt: title
        }
      ],
      site_name: 'TalentProfile'
    },
    twitter: {
      card: 'summary_large_image' as const,
      site: defaultSEO.twitter.site,
      title,
      description,
      image: fullOgImage
    },
    ...(link.length > 0 && { link }),
    structuredData: structuredDataArray
  };
}

function stripContext<T extends { '@context'?: unknown }>(schema: T): Omit<T, '@context'> {
	const { '@context': _, ...rest } = schema;
	return rest as Omit<T, '@context'>;
}

interface RolePageSEOJob {
  id: string;
  slug?: string | null;
  title: string;
  shortParaphrasedDescription?: string | null;
  salary?: string | null;
  salaryCurrency?: string | null;
  salaryFrequency?: string | null;
  employmentType?: string | null;
  remoteType?: string | null;
  location?: string | null;
  city?: string | null;
  keywords?: string[] | null;
  country?: string | null;
  createdAt: string;
  deletedAt?: string | null;
  postedAt?: string | null;
  company?: { name: string; logo?: string | null; website?: string | null } | null;
  company_name?: string | null;
  company_logo?: string | null;
}

export function createRolePageSEO(params: {
	roleSlug: string;
	roleMeta: {
		label: string;
		title: string;
		description: string;
		faqs: Array<{ question: string; answer: string }>;
	};
	currentPage: number;
	totalPages?: number;
	jobs: RolePageSEOJob[];
}): ReturnType<typeof createPageSEO> {
	const { roleSlug, roleMeta, currentPage, jobs } = params;
	const totalPages = params.totalPages ?? 1;
	const isFirstPage = currentPage === 1;
	const basePath = `/jobs/${roleSlug}`;
	const canonicalPath = isFirstPage ? basePath : `${basePath}?page=${currentPage}`;

	const link: Array<{ rel: string; href: string }> = [];
	if (!isFirstPage) {
		link.push({
			rel: 'prev',
			href: currentPage === 2 ? `${PUBLIC_URL}${basePath}` : `${PUBLIC_URL}${basePath}?page=${currentPage - 1}`
		});
	}
	if (currentPage < totalPages) {
		link.push({
			rel: 'next',
			href: `${PUBLIC_URL}${basePath}?page=${currentPage + 1}`
		});
	}

	const additionalMetaTags: Array<{ name: string; content: string }> = [];
	if (!isFirstPage) {
		additionalMetaTags.push({ name: 'robots', content: 'noindex,follow' });
	}

	const description = isFirstPage
		? roleMeta.description
		: `Browse page ${currentPage} of ${totalPages} for ${roleMeta.label.toLowerCase()} developer jobs. ${roleMeta.description}`;

	const breadcrumbs = createBreadcrumbSchema([
		{ name: 'Home', url: '/' },
		{ name: 'Jobs', url: '/jobs' },
		{ name: roleMeta.label }
	]);

	const faqSchema = createFAQSchema(roleMeta.faqs);

	const structuredData: StructuredData[] = [breadcrumbs, faqSchema];

	if (jobs.length > 0) {
		const jobListings: WithContext<ItemList> = {
			'@context': 'https://schema.org',
			'@type': 'ItemList',
			name: `${roleMeta.label} Jobs${currentPage > 1 ? ` - Page ${currentPage}` : ''}`,
			numberOfItems: jobs.length,
			itemListElement: jobs
				.filter((job) => !job.deletedAt)
				.map((job, idx) => {
					const jobPosting = createJobPostingSchema({
						title: job.title,
						description: job.shortParaphrasedDescription || job.title,
						salary: job.salary,
						salaryCurrency: job.salaryCurrency,
						salaryFrequency: job.salaryFrequency,
						employmentType: job.employmentType,
						remoteType: job.remoteType,
						location: job.location,
						city: job.city,
						country: job.country,
						url: `${PUBLIC_URL}/job/${job.slug || job.id}`,
						postedAt: (job.postedAt || job.createdAt).split('T')[0],
						companyName: job.company?.name || job.company_name || 'Hiring Company',
						companyLogo: cdnUrlToString(job.company?.logo || job.company_logo),
						companyWebsite: job.company?.website || undefined
					});

					return {
						'@type': 'ListItem',
						position: idx + 1,
						item: stripContext(jobPosting)
					};
				})
		};
		structuredData.push(jobListings);
	}

	return createPageSEO({
		title: `${roleMeta.title}${currentPage > 1 ? ` - Page ${currentPage}` : ''} | TalentProfile`,
		description,
		canonical: canonicalPath,
		structuredData,
		ogImage: `${PUBLIC_URL}/talentprofile-jobs-og.png`,
		link,
		additionalMetaTags
	});
}

export function createErrorPageSEO(status: number, message?: string) {
  const errorConfigs: Record<number, {
    title: string;
    description: string;
    robots: string;
  }> = {
    404: {
      title: 'Page Not Found (404) | TalentProfile',
      description: 'The page you\'re looking for doesn\'t exist or has been moved. Browse our job listings or return to the homepage.',
      robots: 'noindex,follow'
    },
    403: {
      title: 'Access Denied (403) | TalentProfile',
      description: 'You don\'t have permission to access this page. Please contact support if you believe this is an error.',
      robots: 'noindex,nofollow'
    },
    500: {
      title: 'Server Error (500) | TalentProfile',
      description: 'Something went wrong on our end. We\'re working to fix it. Please try again in a moment.',
      robots: 'noindex,nofollow'
    }
  };

  const config = errorConfigs[status] || {
    title: `Error ${status} | TalentProfile`,
    description: message || 'An error occurred. Please try again or contact support.',
    robots: 'noindex,nofollow'
  };

  const structuredData = createWebPageSchema({
    name: config.title,
    description: config.description,
    url: '/'
  });

  return createPageSEO({
    title: config.title,
    description: config.description,
    ogImage: '/error.png',
    structuredData,
    additionalMetaTags: [
      { name: 'robots', content: config.robots },
      { name: 'googlebot', content: config.robots }
    ]
  });
}

export function createWebPageSchema(options: {
  name: string;
  description: string;
  url?: string;
}): WithContext<WebPage> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": options.name,
    "description": options.description,
    ...(options.url && { "url": `${PUBLIC_URL}${options.url}` }),
    "provider": {
      "@type": "Organization",
      "name": "TalentProfile",
      "url": PUBLIC_URL
    }
  };
}

export function createOrganizationSchema(): WithContext<Organization> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "TalentProfile",
    "url": PUBLIC_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${PUBLIC_URL}/web-app-manifest-512x512.png`,
      "width": "512",
      "height": "512"
    },
    "description": "AI-powered tech talent matching platform",
    "foundingDate": "2025",
    "sameAs": []
  };
}

export function createBreadcrumbSchema(items: Array<{ name: string; url?: string }>): WithContext<BreadcrumbList> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.url && { "item": `${PUBLIC_URL}${item.url}` })
    }))
  };
}

export function createFAQSchema(faqs: Array<{ question: string; answer: string }>): WithContext<FAQPage> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

export function createHowToSchema(options: {
  name: string;
  description: string;
  steps: Array<{
    name: string;
    text: string;
    url?: string;
  }>;
  totalTime?: string;
}): WithContext<HowTo> {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": options.name,
    "description": options.description,
    "step": options.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      ...(step.url && { "url": `${PUBLIC_URL}${step.url}` })
    })),
    ...(options.totalTime && { "totalTime": options.totalTime })
  };
}

export function createSoftwareApplicationSchema(options: {
  name: string;
  description?: string;
  applicationCategory: string;
  operatingSystem?: string;
  price?: string;
  priceCurrency?: string;
  aggregateRating?: {
    ratingValue: string | number;
    ratingCount: number;
    reviewCount?: number;
  };
}): WithContext<SoftwareApplication> {
  const schema: WithContext<SoftwareApplication> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": options.name,
    "applicationCategory": options.applicationCategory,
  };

  if (options.description) {
    schema.description = options.description;
  }

  if (options.operatingSystem) {
    schema.operatingSystem = options.operatingSystem;
  }

  if (options.price !== undefined) {
    schema.offers = {
      "@type": "Offer",
      "price": options.price,
      "priceCurrency": options.priceCurrency || "USD"
    };
  }

  if (options.aggregateRating) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": options.aggregateRating.ratingValue,
      "ratingCount": options.aggregateRating.ratingCount,
      ...(options.aggregateRating.reviewCount && {
        "reviewCount": options.aggregateRating.reviewCount
      })
    };
  }

  return schema;
}

export function createItemListSchema(options: {
  name: string;
  description?: string;
  items: Array<{
    name: string;
    description: string;
  }>;
}): WithContext<ItemList> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": options.name,
    ...(options.description && { "description": options.description }),
    "itemListElement": options.items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Thing",
        "name": item.name,
        "description": item.description
      }
    }))
  };
}

export function createJobPostingSchema(job: {
  title: string;
  description: string;
  salary?: string | null;
  salaryCurrency?: string | null;
  salaryFrequency?: string | null;
  employmentType?: string | null;
  remoteType?: string | null;
  location?: string | null;
  city?: string | null;
  country?: string | null;
  url?: string | null;
  postedAt?: string | null;
  deletedAt?: string | null;
  companyName?: string;
  companyWebsite?: string | null;
  companyLogo?: string | null;
  roleCategory?: string | null;
}): WithContext<JobPosting> {
  const schema: WithContext<JobPosting> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    directApply: false,
  };

  if (job.url) {
    schema.identifier = {
      "@type": "PropertyValue",
      name: "Job URL",
      value: job.url
    };
  }

  if (job.roleCategory) {
    schema.industry = job.roleCategory;
  }

  const postedDate = safeParseDate(job.postedAt);
  if (postedDate) {
    schema.datePosted = job.postedAt!;

    const deletedDate = safeParseDate(job.deletedAt);
    if (deletedDate) {
      schema.validThrough = deletedDate.toISOString().split('T')[0];
    } else {
      schema.validThrough = new Date(postedDate.getTime() + 90 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
    }
  }

  if (job.employmentType) {
    const mapped = EMPLOYMENT_TYPE_SCHEMA[job.employmentType.toLowerCase()];
    if (mapped) {
      schema.employmentType = mapped as JobPosting['employmentType'];
    }
  }

  if (job.remoteType) {
    const mapped = REMOTE_TYPE_SCHEMA[job.remoteType];
    if (mapped) {
      schema.jobLocationType = mapped as JobPosting['jobLocationType'];
    }
  }

  if (job.companyName) {
    const org: Organization = {
      "@type": "Organization",
      name: job.companyName
    };
    if (job.companyLogo) org.logo = job.companyLogo;
    if (job.companyWebsite) org.url = job.companyWebsite;
    schema.hiringOrganization = org;
  }

  if (job.location || job.city || job.country) {
    const address: PostalAddress = { "@type": "PostalAddress" };
    if (job.city) address.addressLocality = job.city;
    if (job.country) address.addressCountry = job.country;
    const place: Place = { "@type": "Place", address };
    schema.jobLocation = place;

    if (job.country) {
      schema.applicantLocationRequirements = {
        "@type": "Country",
        name: job.country
      };
    }
  }

  if (job.salary) {
    const frequency = job.salaryFrequency?.toUpperCase() || 'YEAR';
    const unitMap: Record<string, string> = {
      YEAR: 'YEAR',
      YEARLY: 'YEAR',
      ANNUAL: 'YEAR',
      MONTH: 'MONTH',
      MONTHLY: 'MONTH',
      HOUR: 'HOUR',
      HOURLY: 'HOUR',
      WEEK: 'WEEK',
      WEEKLY: 'WEEK',
    };

    schema.baseSalary = {
      "@type": "MonetaryAmount",
      currency: job.salaryCurrency || "USD",
      value: {
        "@type": "QuantitativeValue",
        value: job.salary,
        unitText: unitMap[frequency] || 'YEAR'
      }
    };
  }

  if (job.url) schema.url = job.url;

  return schema;
}

export function createJobsPageSEO(params: {
  currentPage: number;
  totalPages: number;
  totalJobs: number;
  jobsOnThisPage: RolePageSEOJob[];
  baseCanonical?: string;
  indexPaginatedPages?: boolean;
}) {
  const page = params.currentPage;
  const isFirstPage = page === 1;
  const base = params.baseCanonical || '/jobs';
  const canonicalPath = isFirstPage ? base : `${base}?page=${page}`;
  const indexPaginated = params.indexPaginatedPages ?? false;

  const link: Array<{ rel: string; href: string }> = [];
  if (!isFirstPage) {
    link.push({
      rel: 'prev',
      href: page === 2 ? `${PUBLIC_URL}${base}` : `${PUBLIC_URL}${base}?page=${page - 1}`
    });
  }
  if (page < params.totalPages) {
    link.push({
      rel: 'next',
      href: `${PUBLIC_URL}${base}?page=${page + 1}`
    });
  }

  const allKeywords = new Set<string>();
  const locationSet = new Set<string>();
  const remoteTypes = new Set<string>();
  const employmentTypes = new Set<string>();

  params.jobsOnThisPage.forEach(job => {
    const keywords = job.keywords || [];
    keywords.forEach(k => allKeywords.add(k));

    if (job.city) locationSet.add(job.city);
    if (job.remoteType) remoteTypes.add(job.remoteType);
    if (job.employmentType) employmentTypes.add(job.employmentType);
  });

  const topKeywords = Array.from(allKeywords).slice(0, 20);
  const locations = Array.from(locationSet).slice(0, 5);
  const uniqueRemoteTypes = Array.from(remoteTypes);
  const uniqueEmploymentTypes = Array.from(employmentTypes);

  const activeJobs = params.jobsOnThisPage.filter((job) => !job.deletedAt);

  const itemList: WithContext<ItemList> = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: isFirstPage ? "Tech Job Opportunities" : `Tech Jobs – Page ${page}`,
    description: isFirstPage
      ? `Browse ${params.totalJobs.toLocaleString()} verified tech job openings`
      : `Page ${page} of ${params.totalPages} tech job listings`,
    numberOfItems: activeJobs.length,
    itemListElement: activeJobs.map((job, idx) => {
      const jobPosting = createJobPostingSchema({
        title: job.title,
        description: job.shortParaphrasedDescription || job.title,
        salary: job.salary,
        salaryCurrency: job.salaryCurrency,
        salaryFrequency: job.salaryFrequency,
        employmentType: job.employmentType,
        remoteType: job.remoteType,
        location: job.location,
        city: job.city,
        country: job.country,
        url: `${PUBLIC_URL}/job/${job.slug || job.id}`,
        postedAt: (job.postedAt || job.createdAt).split('T')[0],
        companyName: job.company?.name || job.company_name || 'Hiring Company',
        companyLogo: cdnUrlToString(job.company?.logo || job.company_logo),
        companyWebsite: job.company?.website || undefined
      });

      return {
        "@type": "ListItem",
        position: idx + 1 + (page - 1) * 50,
        item: jobPosting
      };
    })
  };

  const breadcrumbs = createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Jobs', url: base },
    ...(page > 1 ? [{ name: `Page ${page}` }] : [])
  ]);

  const topSkills = topKeywords.slice(0, 3).join(', ');
  const remoteTypeText = formatRemoteTypesForTitle(uniqueRemoteTypes);

  const title = isFirstPage
    ? topSkills
      ? `${params.totalJobs.toLocaleString()}+ Tech Jobs: ${topSkills} & More${remoteTypeText} | TalentProfile`
      : `${params.totalJobs.toLocaleString()}+ Tech Jobs${remoteTypeText} | TalentProfile`
    : topSkills
      ? `Tech Jobs Page ${page}/${params.totalPages}: ${topSkills}${remoteTypeText} | TalentProfile`
      : `Tech Jobs Page ${page}/${params.totalPages}${remoteTypeText} | TalentProfile`;

  const locationText = locations.length > 0 ? ` in ${locations.join(', ')}` : '';
  const employmentText = uniqueEmploymentTypes.length > 0
    ? ` • ${uniqueEmploymentTypes.join(', ')} positions`
    : '';

  const description = isFirstPage
    ? topSkills
      ? `Find your next role from ${params.totalJobs.toLocaleString()} tech jobs${locationText}. Featuring ${topSkills} and more${employmentText}. AI-matched opportunities updated daily.`
      : `Find your next role from ${params.totalJobs.toLocaleString()} tech jobs${locationText}${employmentText}. AI-matched opportunities updated daily.`
    : topSkills
      ? `Browse page ${page} of ${params.totalPages}: ${topSkills} jobs${locationText}${employmentText}. ${params.totalJobs.toLocaleString()} total positions available.`
      : `Browse page ${page} of ${params.totalPages}${locationText}${employmentText}. ${params.totalJobs.toLocaleString()} total positions available.`;

  const baseKeywords = [
    'tech jobs',
    'software engineer jobs',
    'developer jobs',
    'remote tech jobs',
    'hybrid jobs',
    'on-site tech positions'
  ];

  const dynamicKeywords = [
    ...topKeywords.slice(0, 12),
    ...locations.map(loc => `${loc} tech jobs`),
    ...formatRemoteTypesForKeywords(uniqueRemoteTypes)
  ];

  const keywords = isFirstPage
    ? [...baseKeywords, ...dynamicKeywords].join(', ')
    : [...dynamicKeywords, `tech jobs page ${page}`].join(', ');

  const structuredData: StructuredData[] = [
    createWebPageSchema({
      name: isFirstPage ? "Tech Job Board" : `Tech Jobs – Page ${page}`,
      description,
      url: canonicalPath
    }),
    breadcrumbs,
    itemList
  ];

  if (isFirstPage) {
    structuredData.push(createOrganizationSchema());
  }

  const additionalMetaTags: Array<{ name: string; content: string }> = [
    { name: 'keywords', content: keywords },
    { name: 'author', content: 'TalentProfile' },
    { name: 'revised', content: new Date().toISOString() },
    { name: 'mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'application-name', content: 'TalentProfile Jobs' },

    ...(locations.length > 0 ? [
      { name: 'geo.region', content: locations[0] }
    ] : []),

    { name: 'job-posting-count', content: String(params.jobsOnThisPage.length) },
  ];

  if (page > 1 && !indexPaginated) {
    additionalMetaTags.push({ name: 'robots', content: 'noindex,follow' });
  } else {
    additionalMetaTags.push({ name: 'robots', content: 'index,follow,max-image-preview:large' });
  }

  return createPageSEO({
    title,
    description,
    canonical: canonicalPath,
    keywords,
    link,
    structuredData,
    additionalMetaTags,
    ogImage: `${PUBLIC_URL}/talentprofile-jobs-og.png`,
  });
}